import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import RequestsTab from "./RequestsTab";
import PortfolioTab from "./PortfolioTab";
import CategoriesTab from "./CategoriesTab";
import SettingsTab from "./SettingsTab";
import BlogTab from "./BlogTab";
import TemplatesTab from "./TemplatesTab";
import WorkshopsTab from "./WorkshopsTab";
import CalendarTab from "./CalendarTab";

import GiftCardsTab from "./GiftCardsTab";
import AnalyticsTab from "./AnalyticsTab";
import ClientsTab from "./ClientsTab";
import ReviewsTab from "./ReviewsTab";
import LearnTab from "./LearnTab";

const TABS = [
  { id: "bookings", label: "Photography Bookings" },
  { id: "clients", label: "Clients" },
  { id: "calendar", label: "Calendar" },
  { id: "analytics", label: "Analytics" },
  { id: "classes", label: "Class Requests" },
  { id: "learn", label: "Learn Offerings" },
  { id: "leads", label: "Real Estate Leads" },
  { id: "inquiries", label: "Inquiries" },
  { id: "templates", label: "Forms & Contracts" },
  { id: "portfolio", label: "Portfolio" },
  { id: "categories", label: "Categories" },
  { id: "blog", label: "Blog" },
  { id: "workshops", label: "Workshops" },

  { id: "giftcards", label: "Gift Cards" },
  { id: "reviews", label: "Reviews" },
  { id: "settings", label: "Settings" },
];

export default function Admin() {
  const [authed, setAuthed] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [tab, setTab] = useState("bookings");

  useEffect(() => {
    if (!localStorage.getItem("bs_token")) { setAuthed(false); return; }
    api.get("/auth/me").then(() => setAuthed(true)).catch(() => { localStorage.removeItem("bs_token"); setAuthed(false); });
  }, []);

  const login = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post("/auth/login", { email, password });
      localStorage.setItem("bs_token", r.data.token);
      setAuthed(true);
      toast.success("Welcome back, Bahare");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const logout = () => {
    localStorage.removeItem("bs_token");
    setAuthed(false);
  };

  if (authed === null) return <div className="min-h-screen bg-[#F9F9F8]" />;

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#F9F9F8] flex items-center justify-center px-6" data-testid="admin-login-page">
        <form onSubmit={login} className="w-full max-w-sm bg-white border border-[#E5E5E2] p-10">
          <p className="font-serif text-2xl mb-1 italic">Bahare</p>
          <p className="text-xs text-[#7A7A7A] mb-8 uppercase tracking-[0.2em]">Admin Dashboard</p>
          <input type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)}
            data-testid="admin-login-email" className="w-full border border-[#E5E5E2] px-4 py-3 text-sm mb-4 outline-none focus:border-[#1A1A1A]" />
          <input type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)}
            data-testid="admin-login-password" className="w-full border border-[#E5E5E2] px-4 py-3 text-sm mb-6 outline-none focus:border-[#1A1A1A]" />
          <button type="submit" data-testid="admin-login-submit"
            className="w-full bg-[#1A1A1A] text-white text-xs uppercase tracking-[0.2em] py-3.5 hover:bg-[#4A4A4A] transition-colors">
            Sign In
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F4F1]" data-testid="admin-dashboard">
      <header className="bg-white border-b border-[#E5E5E2] sticky top-0 z-40">
        <div className="max-w-[1200px] mx-auto px-6 h-16 flex items-center justify-between">
          <p className="font-serif text-xl italic">Bahare <span className="text-xs text-[#7A7A7A] font-sans not-italic uppercase tracking-[0.2em] ml-2">Admin</span></p>
          <div className="flex items-center gap-4">
            <a href="/" className="text-xs text-[#7A7A7A] hover:text-[#1A1A1A] uppercase tracking-[0.15em]">View Site</a>
            <button onClick={logout} data-testid="admin-logout" className="text-xs uppercase tracking-[0.15em] border border-[#E5E5E2] px-4 py-2 hover:border-[#1A1A1A]">Log out</button>
          </div>
        </div>
        <div className="max-w-[1200px] mx-auto px-6 flex gap-1 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
              className={`px-4 py-3 text-xs uppercase tracking-[0.15em] whitespace-nowrap border-b-2 transition-colors ${tab === t.id ? "border-[#1A1A1A] text-[#1A1A1A]" : "border-transparent text-[#7A7A7A] hover:text-[#1A1A1A]"}`}>
              {t.label}
            </button>
          ))}
        </div>
      </header>
      <main className="max-w-[1200px] mx-auto px-6 py-10">
        {tab === "bookings" && (
          <RequestsTab endpoint="/admin/bookings" testId="bookings"
            statuses={["New", "Contacted", "Pending", "Booked", "Completed", "Declined", "Canceled"]}
            recordHref={(r) => `/admin/client/${r.id}`}
            nameOf={(r) => r.name || `${r.first_name || ""} ${r.last_name || ""}`.trim()}
            details={(r) => [
              ["Category", r.category], ["Session", r.session_date ? `${r.session_date}${r.session_time ? ` · ${r.session_time}` : ""}` : r.preferred_date],
              ["Package", r.package], ["Price", r.price && `$${r.price}`], ["Paid", r.deposit && `$${r.deposit}`],
              ["Balance", r.balance && `$${r.balance}`], ["Pay Method", r.payment_method],
              ["Payment Memo", r.payment_memo],
              ["Preferred Date", r.preferred_date && !r.session_date ? r.preferred_date : ""], ["Time", !r.session_date && r.preferred_time],
              ["People", r.num_people], ["Location", r.session_location || r.location],
              ["Instagram", r.instagram], ["Notes", r.message], ["Heard Via", r.referral],
            ]} />
        )}
        {tab === "classes" && (
          <RequestsTab endpoint="/admin/class-requests" testId="classes"
            statuses={["New", "Contacted", "Pending", "Paid — Scheduling Pending", "Booked", "Completed", "Declined"]}
            nameOf={(r) => r.name}
            details={(r) => [
              ["Learn", r.learn], ["Device", r.device], ["Class Type", r.class_type], ["Experience", r.experience],
              ["Preferred Date", r.preferred_date], ["Session", r.session_date ? `${r.session_date}${r.session_time ? ` · ${r.session_time}` : ""}` : ""],
              ["Location", r.preferred_location || r.location], ["Instagram", r.instagram], ["Message", r.message],
              ["Price", r.price_total], ["Paid", r.amount_paid != null ? `$${r.amount_paid}` : ""], ["Payment", r.payment_status],
            ]} />
        )}
        {tab === "learn" && <LearnTab />}
        {tab === "leads" && (
          <RequestsTab endpoint="/admin/realestate-leads" testId="leads"
            statuses={["New", "Contacted", "Follow Up", "Active Client", "Closed", "Not Interested"]}
            nameOf={(r) => r.name}
            details={(r) => [
              ["Interest", r.interest], ["Area", r.area], ["Budget", r.budget], ["Timeline", r.timeline], ["Message", r.message],
            ]} />
        )}
        {tab === "calendar" && <CalendarTab />}
        {tab === "clients" && <ClientsTab />}
        {tab === "analytics" && <AnalyticsTab />}
        {tab === "inquiries" && (
          <RequestsTab endpoint="/admin/inquiries" testId="inquiries"
            statuses={["New", "Contacted", "Closed"]}
            nameOf={(r) => r.name}
            details={(r) => [["Phone", r.phone], ["Message", r.message]]} />
        )}
        {tab === "portfolio" && <PortfolioTab />}
        {tab === "templates" && <TemplatesTab />}
        {tab === "categories" && <CategoriesTab />}
        {tab === "blog" && <BlogTab />}
        {tab === "workshops" && <WorkshopsTab />}
        {tab === "giftcards" && <GiftCardsTab />}
        {tab === "reviews" && <ReviewsTab />}
        {tab === "settings" && <SettingsTab />}
      </main>
    </div>
  );
}
